import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * DataVisualizationAnimation - 数据可视化动画效果
 * 
 * 适用场景：
 * - 统计数据展示
 * - 科普数据可视化
 * - 研究成果展示
 * - 报告数据演示
 * 
 * 教育价值评分：9/10
 * - 将抽象数据可视化
 * - 增强数据理解
 * - 适合科学和经济学科
 */

interface DataPoint {
  label: string;
  value: number;
  color?: string;
}

export type DataVisualizationAnimationProps = {
  title?: string;
  subtitle?: string;
  data?: DataPoint[];
  chartType?: 'bar' | 'line' | 'pie';
  primaryColor?: string;
  backgroundColor?: string;
  showValues?: boolean;
  maxValue?: number;
};

export const DataVisualizationAnimation: React.FC<DataVisualizationAnimationProps> = ({
  title = '',
  subtitle,
  data = [],
  chartType = 'bar',
  primaryColor = '#4A90E2',
  backgroundColor = '#0F1419',
  showValues = true,
  maxValue: customMaxValue,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const maxValue = customMaxValue || Math.max(...data.map(d => d.value));

  // 标题动画
  const titleOpacity = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  // 图表动画进度
  const chartProgress = interpolate(frame, [30, 90], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 默认颜色调色板
  const colorPalette = [
    '#4A90E2',
    '#7B68EE',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#06B6D4',
  ];

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        padding: 60,
      }}
    >
      {/* 标题区域 */}
      <div
        style={{
          opacity: titleOpacity,
          marginBottom: 50,
        }}
      >
        <h1
          style={{
            fontSize: 56,
            fontWeight: 700,
            color: '#FFFFFF',
            margin: 0,
            textShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              fontSize: 28,
              color: '#A0AEC0',
              marginTop: 12,
              maxWidth: '70%',
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* 图表容器 */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {chartType === 'bar' && (
          <BarChart
            data={data}
            colorPalette={colorPalette}
            maxValue={maxValue}
            progress={chartProgress}
            showValues={showValues}
            width={width * 0.8}
            height={height * 0.5}
          />
        )}

        {chartType === 'line' && (
          <div style={{ marginTop: -80 }}>
            <LineChart
              data={data}
              colorPalette={colorPalette}
              maxValue={maxValue}
              progress={chartProgress}
              showValues={showValues}
              width={width * 0.8}
              height={height * 0.5}
            />
          </div>
        )}

        {chartType === 'pie' && (
          <div style={{ marginTop: -40 }}>
            <PieChart
              data={data}
              colorPalette={colorPalette}
              progress={chartProgress}
              showValues={showValues}
              size={Math.min(width, height) * 0.5}
            />
          </div>
        )}
      </div>

      {/* 数据来源说明 */}
      <div
        style={{
          textAlign: 'center',
          fontSize: 24,
          color: '#718096',
          marginTop: 30,
          opacity: interpolate(frame, [100, 120], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        数据来源：示例数据（仅作演示用途）
      </div>
    </AbsoluteFill>
  );
};

// 柱状图组件
const BarChart: React.FC<{
  data: DataPoint[];
  colorPalette: string[];
  maxValue: number;
  progress: number;
  showValues: boolean;
  width: number;
  height: number;
}> = ({ data, colorPalette, maxValue, progress, showValues, width, height }) => {
  const barWidth = width / data.length * 0.6;
  const gap = width / data.length * 0.4;

  return (
    <div
      style={{
        width,
        height,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-around',
        paddingBottom: 60,
      }}
    >
      {data.map((point, index) => {
        const barHeight = (point.value / maxValue) * height * 0.8;
        const animatedHeight = barHeight * progress;
        const color = point.color || colorPalette[index % colorPalette.length];

        const barSpring = spring({
          frame: Math.floor(progress * 60) - index * 5,
          fps: 30,
          config: { damping: 12, stiffness: 80 },
        });

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            {/* 数值标签 */}
            {showValues && (
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#FFFFFF',
                  opacity: progress > 0.8 ? 1 : 0,
                  transform: `translateY(${(1 - progress) * 20}px)`,
                }}
              >
                {point.value}
              </div>
            )}

            {/* 柱子 */}
            <div
              style={{
                width: barWidth,
                height: animatedHeight,
                background: `linear-gradient(180deg, ${color} 0%, ${color}CC 100%)`,
                borderRadius: '8px 8px 0 0',
                boxShadow: `0 0 20px ${color}40`,
                transform: `scaleY(${barSpring})`,
                transformOrigin: 'bottom',
              }}
            />

            {/* 标签 */}
            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: '#A0AEC0',
                textAlign: 'center',
                maxWidth: barWidth + gap,
                opacity: progress > 0.5 ? 1 : 0,
              }}
            >
              {point.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 折线图组件
const LineChart: React.FC<{
  data: DataPoint[];
  colorPalette: string[];
  maxValue: number;
  progress: number;
  showValues: boolean;
  width: number;
  height: number;
}> = ({ data, colorPalette, maxValue, progress, showValues, width, height }) => {
  const pointSpacing = width / (data.length - 1);
  const chartHeight = height * 0.8;

  // 生成路径
  const points = data.map((point, index) => {
    const x = index * pointSpacing;
    const y = chartHeight - (point.value / maxValue) * chartHeight;
    return { x, y, ...point };
  });

  const visiblePoints = Math.ceil(data.length * progress);

  return (
    <div style={{ width, height, position: 'relative' }}>
      {/* 网格线 */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: 0,
            top: chartHeight * (1 - ratio),
            width: '100%',
            height: 1,
            background: 'rgba(255, 255, 255, 0.1)',
          }}
        />
      ))}

      {/* SVG 路径 */}
      <svg
        width={width}
        height={chartHeight}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* 渐变定义 */}
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colorPalette[0]} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colorPalette[0]} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* 填充区域 */}
        <path
          d={`
            M 0 ${chartHeight}
            L ${points.slice(0, visiblePoints).map(p => `${p.x} ${p.y}`).join(' L ')}
            L ${Math.min(visiblePoints - 1, data.length - 1) * pointSpacing} ${chartHeight}
            Z
          `}
          fill="url(#lineGradient)"
        />

        {/* 线条 */}
        <path
          d={`M ${points.slice(0, visiblePoints).map(p => `${p.x} ${p.y}`).join(' L ')}`}
          stroke={colorPalette[0]}
          strokeWidth={3}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* 数据点 */}
        {points.slice(0, visiblePoints).map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r={8}
              fill={colorPalette[0]}
              stroke="#FFFFFF"
              strokeWidth={3}
            />
            {showValues && (
              <text
                x={point.x}
                y={point.y - 20}
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize={20}
                fontWeight={600}
              >
                {point.value}
              </text>
            )}
          </g>
        ))}
      </svg>

      {/* X轴标签 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 10,
        }}
      >
        {data.map((point, index) => (
          <div
            key={index}
            style={{
              fontSize: 22,
              color: '#A0AEC0',
              opacity: index < visiblePoints ? 1 : 0.3,
            }}
          >
            {point.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// 饼图组件
const PieChart: React.FC<{
  data: DataPoint[];
  colorPalette: string[];
  progress: number;
  showValues: boolean;
  size: number;
}> = ({ data, colorPalette, progress, showValues, size }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = size / 2;
  const centerX = radius;
  const centerY = radius;

  let currentAngle = -90;

  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      <svg width={size} height={size}>
        {data.map((point, index) => {
          const percentage = point.value / total;
          const angle = percentage * 360 * progress;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;

          const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
          const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
          const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
          const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

          const largeArcFlag = angle > 180 ? 1 : 0;

          const pathD = `
            M ${centerX} ${centerY}
            L ${x1} ${y1}
            A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
            Z
          `;

          const color = point.color || colorPalette[index % colorPalette.length];

          currentAngle = endAngle;

          const springProgress = spring({
            frame: Math.floor(progress * 60) - index * 8,
            fps: 30,
            config: { damping: 12, stiffness: 60 },
          });

          return (
            <g key={index}>
              <path
                d={pathD}
                fill={color}
                stroke="#0F1419"
                strokeWidth={3}
                opacity={springProgress}
                transform={`scale(${springProgress})`}
                transform-origin={`${centerX}px ${centerY}px`}
              />

              {/* 标签 */}
              {showValues && progress > 0.8 && (
                <text
                  x={centerX + radius * 0.7 * Math.cos(((startAngle + angle / 2) * Math.PI) / 180)}
                  y={centerY + radius * 0.7 * Math.sin(((startAngle + angle / 2) * Math.PI) / 180)}
                  textAnchor="middle"
                  fill="#FFFFFF"
                  fontSize={28}
                  fontWeight={700}
                >
                  {`${Math.round(percentage * 100)}%`}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* 图例 */}
      <div
        style={{
          position: 'absolute',
          right: -280,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {data.map((point, index) => {
          const color = point.color || colorPalette[index % colorPalette.length];
          const legendProgress = spring({
            frame: Math.floor(progress * 60) - index * 5,
            fps: 30,
            config: { damping: 12 },
          });

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                opacity: legendProgress,
                transform: `translateX(${(1 - legendProgress) * 20}px)`,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: color,
                }}
              />
              <span style={{ fontSize: 28, color: '#FFFFFF', fontWeight: 600 }}>{point.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 示例配置
export const exampleDataPoints: DataPoint[] = [
  { label: '2020', value: 450 },
  { label: '2021', value: 680 },
  { label: '2022', value: 920 },
  { label: '2023', value: 1250 },
  { label: '2024', value: 1680 },
];

export const examplePieData: DataPoint[] = [
  { label: '技术', value: 35 },
  { label: '市场', value: 25 },
  { label: '运营', value: 20 },
  { label: '研发', value: 20 },
];

// Composition 配置
export const dataVisualizationAnimationConfig = {
  id: 'DataVisualizationAnimation',
  width: 1920,
  height: 1080,
  durationInFrames: 180,
  fps: 30,
  defaultProps: {
    title: '年度增长趋势',
    subtitle: '2020-2024 年业务增长数据',
    data: exampleDataPoints,
    chartType: 'bar' as const,
    primaryColor: '#4A90E2',
    backgroundColor: '#0F1419',
    showValues: true,
  },
};
